'use client';

import { useState, useEffect } from 'react';
import GoldenTicketStats from './GoldenTicketStats';
import PrizeGallery from './PrizeGallery';
import WinnerShowcase from './WinnerShowcase';
import RedemptionCenter from './RedemptionCenter';
import WishlistManager from './WishlistManager';
import Icon from '@/components/ui/AppIcon';

interface Prize {
  id: number;
  title: string;
  description: string;
  image: string;
  alt: string;
  ticketsRequired: number;
  category: string;
  rarity: 'common' | 'rare' | 'legendary' | 'mythic';
  availability: 'available' | 'limited' | 'sold_out';
  estimatedValue: string;
  redemptionDeadline?: string;
}

interface Winner {
  id: number;
  name: string;
  avatar: string;
  avatarAlt: string;
  prize: string;
  redemptionDate: string;
  testimonial: string;
  ticketsUsed: number;
  memberSince: string;
  totalContributions: number;
}

interface RedemptionItem {
  id: number;
  ticketId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  prizeName: string;
  redemptionDate: string;
  estimatedDelivery?: string;
  trackingNumber?: string;
}

interface WishlistItem {
  id: number;
  title: string;
  image: string;
  alt: string;
  ticketsRequired: number;
  category: string;
  rarity: string;
  availability: string;
  estimatedValue: string;
  addedDate: string;
  notifyWhenAvailable: boolean;
}

const GoldenTicketsInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeSection, setActiveSection] = useState<'prizes' | 'winners' | 'redeem' | 'wishlist'>('prizes');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx:72:6" data-component-path="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx" data-component-line="72" data-component-file="GoldenTicketsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx:73:8" data-component-path="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx" data-component-line="73" data-component-file="GoldenTicketsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%20p-8%22%7D" className="animate-pulse space-y-8 p-8">
          <div data-component-id="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx:74:10" data-component-path="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx" data-component-line="74" data-component-file="GoldenTicketsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20bg-muted%20rounded%20w-1%2F3%22%7D" className="h-8 bg-muted rounded w-1/3"></div>
          <div data-component-id="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx:75:10" data-component-path="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx" data-component-line="75" data-component-file="GoldenTicketsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-4%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) =>
            <div data-component-id="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx:77:12" data-component-path="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx" data-component-line="77" data-component-file="GoldenTicketsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-32%20bg-muted%20rounded-lg%22%7D" key={i} className="h-32 bg-muted rounded-lg"></div>
            )}
          </div>
          <div data-component-id="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx:80:10" data-component-path="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx" data-component-line="80" data-component-file="GoldenTicketsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-muted%20rounded-lg%22%7D" className="h-64 bg-muted rounded-lg"></div>
        </div>
      </div>);

  }

  // Mock data
  const mockPrizes: Prize[] = [
  {
    id: 1,
    title: "Master of Puppets Vinyl Box Set",
    description: "Limited edition 35th anniversary vinyl collection with unreleased tracks, studio outtakes, and exclusive artwork. Includes signed lithograph and certificate of authenticity.",
    image: "https://images.unsplash.com/photo-1544083018-b9df7b619b36",
    alt: "Vintage vinyl records collection with Master of Puppets album artwork displayed on wooden surface",
    ticketsRequired: 5,
    category: "Music Collection",
    rarity: "legendary",
    availability: "available",
    estimatedValue: "$299",
    redemptionDeadline: "Dec 31, 2024"
  },
  {
    id: 2,
    title: "VIP Concert Experience Package",
    description: "Front row seats, backstage meet & greet, exclusive merchandise, and dinner with the band. Includes transportation and luxury hotel accommodation for two nights.",
    image: "https://images.unsplash.com/photo-1646911884128-484da0cf94a2",
    alt: "Concert stage with dramatic lighting and crowd silhouettes during live rock performance",
    ticketsRequired: 10,
    category: "Live Experience",
    rarity: "mythic",
    availability: "limited",
    estimatedValue: "$2,500"
  },
  {
    id: 3,
    title: "Exclusive Digital Archive Access",
    description: "Lifetime access to rare recordings, studio sessions, and behind-the-scenes content. Includes early access to new releases and exclusive digital concerts.",
    image: "https://images.unsplash.com/photo-1583283120976-49e7b48aedbc",
    alt: "Digital music interface showing waveforms and recording studio equipment on computer screen",
    ticketsRequired: 1,
    category: "Digital Content",
    rarity: "common",
    availability: "available",
    estimatedValue: "$99"
  },
  {
    id: 4,
    title: "Signed Guitar Pick Collection",
    description: "Set of 12 authentic guitar picks used during live performances, each signed by band members. Comes with display case and certificate of authenticity.",
    image: "https://images.unsplash.com/photo-1618011452322-1156b2b1da34",
    alt: "Collection of colorful guitar picks arranged in display case with band signatures visible",
    ticketsRequired: 3,
    category: "Memorabilia",
    rarity: "rare",
    availability: "available",
    estimatedValue: "$150"
  },
  {
    id: 5,
    title: "Studio Recording Session",
    description: "Private recording session at Metallica's studio with professional engineers. Record your own version of a Metallica song and receive master recording.",
    image: "https://images.unsplash.com/photo-1654446486451-3d2f73a2abf9",
    alt: "Professional recording studio with mixing console, monitors, and acoustic treatment panels",
    ticketsRequired: 15,
    category: "Experience",
    rarity: "mythic",
    availability: "sold_out",
    estimatedValue: "$5,000"
  },
  {
    id: 6,
    title: "Limited Edition Band T-Shirt",
    description: "Exclusive design available only through golden ticket redemption. Premium quality fabric with unique artwork created specifically for community members.",
    image: "https://images.unsplash.com/photo-1579675109935-a12dd235c97f",
    alt: "Black band t-shirt with metallic silver Metallica logo design laid flat on dark surface",
    ticketsRequired: 2,
    category: "Merchandise",
    rarity: "rare",
    availability: "available",
    estimatedValue: "$75"
  }];


  const mockWinners: Winner[] = [
  {
    id: 1,
    name: "Alex Rodriguez",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1de76dcd1-1763294350404.png",
    avatarAlt: "Professional headshot of Hispanic man with short dark hair wearing black leather jacket",
    prize: "VIP Concert Experience Package",
    redemptionDate: "2024-10-15",
    testimonial: "The meet & greet was incredible! The band was so down to earth and the concert from the front row was absolutely mind-blowing. Worth every ticket!",
    ticketsUsed: 10,
    memberSince: "2019",
    totalContributions: 247
  },
  {
    id: 2,
    name: "Sarah Chen",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_10d60e496-1763295319842.png",
    avatarAlt: "Asian woman with long black hair smiling warmly wearing red blazer in office setting",
    prize: "Master of Puppets Vinyl Box Set",
    redemptionDate: "2024-11-02",
    testimonial: "As a vinyl collector, this box set exceeded all expectations. The unreleased tracks are phenomenal and the packaging is museum quality.",
    ticketsUsed: 5,
    memberSince: "2020",
    totalContributions: 189
  },
  {
    id: 3,
    name: "Marcus Thompson",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13b195c1e-1763294642739.png",
    avatarAlt: "African American man with beard wearing casual gray sweater smiling confidently",
    prize: "Signed Guitar Pick Collection",
    redemptionDate: "2024-11-08",
    testimonial: "These picks are authentic pieces of rock history. You can see the wear from actual performances. The display case looks amazing in my music room.",
    ticketsUsed: 3,
    memberSince: "2021",
    totalContributions: 156
  },
  {
    id: 4,
    name: "Emma Wilson",
    avatar: "https://images.unsplash.com/photo-1717454396563-a8e605c98a86",
    avatarAlt: "Young blonde woman with bright smile wearing white casual top in natural lighting",
    prize: "Limited Edition Band T-Shirt",
    redemptionDate: "2024-11-12",
    testimonial: "The design is absolutely unique and the quality is outstanding. I get compliments every time I wear it. Proud to be part of this community!",
    ticketsUsed: 2,
    memberSince: "2022",
    totalContributions: 98
  }];


  const mockRedemptionHistory: RedemptionItem[] = [
  {
    id: 1,
    ticketId: "GT-2024-001847",
    status: "completed",
    prizeName: "Exclusive Digital Archive Access",
    redemptionDate: "2024-09-15",
    estimatedDelivery: "2024-09-16",
    trackingNumber: "DIG-ACC-789456"
  },
  {
    id: 2,
    ticketId: "GT-2024-002156",
    status: "processing",
    prizeName: "Limited Edition Band T-Shirt",
    redemptionDate: "2024-11-10",
    estimatedDelivery: "2024-11-25",
    trackingNumber: "SHIP-123789456"
  },
  {
    id: 3,
    ticketId: "GT-2024-002298",
    status: "pending",
    prizeName: "Signed Guitar Pick Collection",
    redemptionDate: "2024-11-15"
  }];


  const mockWishlistItems: WishlistItem[] = [
  {
    id: 1,
    title: "Master of Puppets Vinyl Box Set",
    image: "https://images.unsplash.com/photo-1544083018-b9df7b619b36",
    alt: "Vintage vinyl records collection with Master of Puppets album artwork displayed on wooden surface",
    ticketsRequired: 5,
    category: "Music Collection",
    rarity: "legendary",
    availability: "available",
    estimatedValue: "$299",
    addedDate: "2024-10-20",
    notifyWhenAvailable: true
  },
  {
    id: 2,
    title: "Studio Recording Session",
    image: "https://images.unsplash.com/photo-1654446486451-3d2f73a2abf9",
    alt: "Professional recording studio with mixing console, monitors, and acoustic treatment panels",
    ticketsRequired: 15,
    category: "Experience",
    rarity: "mythic",
    availability: "sold_out",
    estimatedValue: "$5,000",
    addedDate: "2024-11-01",
    notifyWhenAvailable: true
  }];


  const navigationItems = [
  { key: 'prizes', label: 'Prize Gallery', icon: 'GiftIcon' },
  { key: 'winners', label: 'Winner Showcase', icon: 'TrophyIcon' },
  { key: 'redeem', label: 'Redemption Center', icon: 'TicketIcon' },
  { key: 'wishlist', label: 'My Wishlist', icon: 'HeartIcon' }];


  return (
    <div data-component-id="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx:279:4" data-component-path="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx" data-component-line="279" data-component-file="GoldenTicketsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-8%22%7D" className="space-y-8">
      {/* Golden Ticket Stats */}
      <GoldenTicketStats data-component-id="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx:281:6" data-component-path="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx" data-component-line="281" data-component-file="GoldenTicketsInteractive.tsx" data-component-name="GoldenTicketStats" data-component-content="%7B%22elementName%22%3A%22GoldenTicketStats%22%7D"
        totalTickets={12847}
        availableTickets={3256}
        redeemedTickets={9591}
        userTickets={7} />


      {/* Section Navigation */}
      <div data-component-id="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx:289:6" data-component-path="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx" data-component-line="289" data-component-file="GoldenTicketsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20border%20border-border%20rounded-lg%20p-2%22%7D" className="bg-card border border-border rounded-lg p-2">
        <div data-component-id="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx:290:8" data-component-path="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx" data-component-line="290" data-component-file="GoldenTicketsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20gap-2%22%7D" className="flex flex-wrap gap-2">
          {navigationItems.map((item) =>
          <button data-component-id="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx:292:10" data-component-path="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx" data-component-line="292" data-component-file="GoldenTicketsInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
            key={item.key}
            onClick={() => setActiveSection(item.key as any)}
            className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-300 ${
            activeSection === item.key ?
            'bg-accent text-accent-foreground shadow-subtle' :
            'text-text-primary hover:bg-muted hover:text-accent'}`
            }>

              <Icon data-component-id="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx:301:14" data-component-path="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx" data-component-line="301" data-component-file="GoldenTicketsInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={item.icon as any} size={16} />
              <span data-component-id="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx:302:14" data-component-path="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx" data-component-line="302" data-component-file="GoldenTicketsInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{item.label}</span>
            </button>
          )}
        </div>
      </div>

      {/* Active Section Content */}
      <div data-component-id="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx:309:6" data-component-path="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx" data-component-line="309" data-component-file="GoldenTicketsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-%5B600px%5D%22%7D" className="min-h-[600px]">
        {activeSection === 'prizes' && <PrizeGallery data-component-id="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx:310:39" data-component-path="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx" data-component-line="310" data-component-file="GoldenTicketsInteractive.tsx" data-component-name="PrizeGallery" data-component-content="%7B%22elementName%22%3A%22PrizeGallery%22%7D" prizes={mockPrizes} />}
        {activeSection === 'winners' && <WinnerShowcase data-component-id="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx:311:40" data-component-path="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx" data-component-line="311" data-component-file="GoldenTicketsInteractive.tsx" data-component-name="WinnerShowcase" data-component-content="%7B%22elementName%22%3A%22WinnerShowcase%22%7D" winners={mockWinners} />}
        {activeSection === 'redeem' &&
        <RedemptionCenter data-component-id="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx:313:8" data-component-path="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx" data-component-line="313" data-component-file="GoldenTicketsInteractive.tsx" data-component-name="RedemptionCenter" data-component-content="%7B%22elementName%22%3A%22RedemptionCenter%22%7D"
          userTickets={7}
          redemptionHistory={mockRedemptionHistory} />

        }
        {activeSection === 'wishlist' && <WishlistManager data-component-id="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx:318:41" data-component-path="src/app/golden-tickets-rewards-hub/components/GoldenTicketsInteractive.tsx" data-component-line="318" data-component-file="GoldenTicketsInteractive.tsx" data-component-name="WishlistManager" data-component-content="%7B%22elementName%22%3A%22WishlistManager%22%7D" wishlistItems={mockWishlistItems} />}
      </div>
    </div>);

};

export default GoldenTicketsInteractive;